/**
 * Central theme registry. Adding a theme is: add a CSS block in globals.css
 * under [data-theme="…"] and an entry here. See
 * menuraq-doc/improve/08-theming-multi-theme.
 */

export type ThemeId = 'light' | 'dark';

/** Persisted preference. Menuraq intentionally offers only Light or Dark. */
export type ThemePreference = ThemeId;

export const THEME_STORAGE_KEY = 'menuraq-theme';

/** Themes whose `color-scheme` is dark; the provider adds the `.dark` class for these. */
export const DARK_THEME_IDS: readonly ThemeId[] = ['dark'];

export interface ThemeMeta {
  id: ThemeId;
  label: string;
  description: string;
  /** Swatch preview colors (any valid CSS color) for the picker UI. */
  swatch: { background: string; primary: string; accent: string };
}

export const THEMES: readonly ThemeMeta[] = [
  {
    id: 'light',
    label: 'Menuraq Light',
    description: 'Soft sage canvas',
    swatch: { background: '#F1F7F2', primary: '#0F382C', accent: '#7CFF6B' },
  },
  {
    id: 'dark',
    label: 'Forest Dark',
    description: 'Deep green low-light mode',
    swatch: { background: '#0B1D16', primary: '#7CFF6B', accent: '#4A8150' },
  },
];

const THEME_IDS = new Set<string>(THEMES.map((t) => t.id));

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && THEME_IDS.has(value);
}

export function isThemePreference(value: unknown): value is ThemePreference {
  return isThemeId(value);
}

export function isDarkTheme(id: ThemeId): boolean {
  return DARK_THEME_IDS.includes(id);
}

/**
 * Resolves a stored preference to the concrete theme id applied to the document.
 */
export function resolveTheme(preference: ThemePreference, _prefersDark: boolean): ThemeId {
  return preference;
}
